/**
 * `requireAuth` — TanStack Router `beforeLoad` loader guard. AUTH-13.
 *
 * Reads the AuthStore synchronously from router context. If the snapshot is
 * already `isAuthenticated`, the loader is a no-op. Otherwise it THROWS a
 * `redirect({to: "/login", search: { redirect: safeRedirectPath(...) }})`
 * so the router cancels the current navigation and routes to /login with
 * the originally-requested path preserved.
 *
 * Open-redirect defense: the `redirect` search param is routed through
 * `safeRedirectPath` (Plan 02) before being serialized — `//evil.com`,
 * `javascript:`, `/%2Fevil`, whitespace, etc. all collapse to `/` before
 * hitting the URL. Plan 05's login screen reads the param, runs it through
 * safeRedirectPath AGAIN on the navigate-back path (belt-and-suspenders
 * per D-06).
 *
 * Usage in a route file (generated via @tanstack/router-plugin):
 *
 *   export const Route = createFileRoute("/employees")({
 *     beforeLoad: requireAuth,
 *     component: EmployeesPage,
 *   });
 */
import { redirect, type ParsedLocation } from "@tanstack/react-router";
import { safeRedirectPath } from "./redirectGuard.js";
import type { RouterContext } from "./routerContext.js";

export interface RequireAuthArgs {
  context: RouterContext;
  location: ParsedLocation;
}

export function requireAuth({ context, location }: RequireAuthArgs): void {
  const snap = context.auth.getSnapshot();
  // A temporary-password session can only change its password: every screen
  // behind the shell would 403. Send it there before the shell renders.
  if (snap.isAuthenticated && snap.mustChangePassword === true) {
    throw redirect({ to: "/auth/change-password", replace: true });
  }
  if (snap.isAuthenticated) return;

  // `location.searchStr` is the "?foo=bar" part including the leading "?"
  // (or empty string when absent). Concatenating with pathname yields the
  // full relative URL the user originally requested.
  const original = location.pathname + (location.searchStr ?? "");
  throw redirect({
    to: "/login",
    search: { redirect: safeRedirectPath(original) },
  });
}
